import { isWritingRequest } from "./writingRequest";

/**
 * Telling Ask Noticeboard what's happening ("School's closed on the 4th",
 * "Photo day moved to Friday") rather than asking it something. Nova can turn
 * an update like that into Drafts, so Ask Noticeboard offers to hand it over.
 *
 * Kept narrow on purpose: a question, a search ("events this week") or a
 * request ("send a notice about Friday") is never an update, and an update
 * says when something happens.
 */

/** Starts like a question. */
const QUESTION_START =
  /^(?:how|where|why|what|whats|what's|which|who|whos|who's|whose|when|is|are|am|do|does|did|have|has|was|were|should|shall|can|could|would|will|may|might|tell me|explain)\b/i;

/** Asks for something to be done or found: "show me", "send a notice", "I need to…". */
const REQUEST_START =
  /^(?:please\s+)?(?:show|find|search|look|open|list|give|get|send|make|create|add|post|publish|write|draft|start|set\s+up|schedule|remind|nudge|resend|delete|remove|cancel|move|change|edit|update|invite|help|check|go|take|let'?s|let\s+us|i\s+(?:need|want|would\s+like|'d\s+like|have)|we\s+(?:need|want|have)\s+to)\b/i;

const WEEKDAY = "monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun";
const MONTH =
  "january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec";

/** Says when: a weekday, a month, "the 4th", "tomorrow", "next week", "12/11", "14:30", "2pm". */
const WHEN = new RegExp(
  [
    `\\b(?:${WEEKDAY})\\b`,
    `\\b(?:${MONTH})\\b`,
    "\\b\\d{1,2}(?:st|nd|rd|th)\\b",
    "\\b(?:today|tonight|tomorrow|tmrw|this\\s+week(?:end)?|next\\s+(?:week(?:end)?|term|month))\\b",
    "\\b\\d{1,2}[/.-]\\d{1,2}(?:[/.-]\\d{2,4})?\\b",
    "\\b\\d{1,2}:\\d{2}\\b",
    "\\b\\d{1,2}\\s?(?:am|pm)\\b",
    "\\bat\\s+\\d{1,2}\\b",
  ].join("|"),
  "gi",
);

/** Words that say nothing about what's happening, so "upcoming events next week" has nothing left. */
const EMPTY_WORDS = new Set(
  (
    "a an the this that these those on at in of for to from by with and or but all our my your " +
    "hi hello hey morning everyone parents " +
    "notice notices event events poll polls draft drafts calendar upcoming scheduled sent " +
    "happening coming up anything something stuff there any last next week weekend month term"
  ).split(" "),
);

/** What's left once the when, and words that say nothing, are taken out. */
const contentWords = (text: string): string[] =>
  text
    .toLowerCase()
    .replace(WHEN, " ")
    .split(/[^a-z0-9'’]+/)
    .filter((word) => word.length > 1 && !EMPTY_WORDS.has(word));

/** Openers before the update itself: "hi all, school closes…". */
const GREETING = /^(?:(?:hi|hello|hey|morning|good\s+morning)(?:\s+(?:all|everyone|team|parents))?[\s,.!:-]+)/i;

export function isNovaUpdate(text: string): boolean {
  const said = text.trim().replace(GREETING, "");
  if (!said || said.includes("?")) return false;
  if (QUESTION_START.test(said) || REQUEST_START.test(said)) return false;
  if (isWritingRequest(said)) return false;
  WHEN.lastIndex = 0;
  if (!WHEN.test(said)) return false;
  return contentWords(said).length >= 2;
}

/** "Nova can do that for you": what's said, and where the button goes. */
export const NOVA_OFFER_REPLY =
  "Nova can do that for you. It reads what's happening and suggests the changes for you to check. Nothing is saved or sent until you confirm.";
